import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Feature } from './capabilities';
import {
  API_URL,
  queryReplace,
  queryCreate,
  queryFetch,
  queryGet,
  queryUpdate,
  queryDelete,
} from './fetch';
import { Offer } from './offers';
import { BillingInformation, BillingMethod } from './quotation';

export enum ProductType {
  FEATURE = 'FEATURE',
  INCENTIVE = 'INCENTIVE',
  PARTICIPANT_FEE = 'PARTICIPANT_FEE',
  SUBSCRIPTION = 'SUBSCRIPTION',
  CREDITS = 'CREDIT',
}

export enum ProductPriceUnit {
  EUR = 'EUR',
  EUR_MONTHLY = 'EUR_MONTHLY', // @deprecated
  EUR_YEARLY = 'EUR_YEARLY', // @deprecated
  EUR_FOR_ONE_HOUR_DURATION = 'EUR_FOR_ONE_HOUR_DURATION',
  INCENTIVE_PERCENT = 'INCENTIVE_PERCENT',
}

export enum OptionName {
  THREE_SCREENING_QUESTIONS = 'THREE_SCREENING_QUESTIONS',
  UNLIMITED_SCREENING_QUESTIONS = 'UNLIMITED_SCREENING_QUESTIONS',
  LIMIT_PARTICIPANTS_TO_10 = 'LIMIT_PARTICIPANTS_TO_10',
  LIMIT_PARTICIPANTS_TO_300 = 'LIMIT_PARTICIPANTS_TO_300',
  NDA_SIGNING = 'NDA_SIGNING',
}

export enum IncentiveMethod {
  PAYPAL = 'PAYPAL',
  GIFT_CARD = 'GIFT_CARD',
  AMAZON = 'AMAZON',
  WEDOOGIFT = 'WEDOOGIFT',
  CUSTOM = 'CUSTOM',
}

export enum PlanStatus {
  DRAFT = 'DRAFT',
  ACCEPTED = 'ACCEPTED',
}

export interface Product {
  id: string;
  name: string | OptionName;
  internalName: string;
  title?: string;
  description?: string;
  type: ProductType;
  pricePerParticipant: boolean;
  priceUnit: ProductPriceUnit;
  price: number;
  category?: string;
  feature?: Feature;
  importantFeature?: boolean;
  version: number;
  active: boolean;
  credits?: number;
  choiceLimit?: number;
  createdAt: Date;
  generated: boolean;
}

export interface ProductLine {
  id: string;
  product?: Product;
  productId: string;
  quantity: number;
  duration?: number;
  unitPrice?: number;
  updatedAt: Date;
  createdAt: Date;
}

export interface Incentive {
  amount: number;
  currencyCode: string;
}

export interface PlanIncentive {
  id: string;
  incentive: Incentive;
  countryKey?: string;
  sessionLinkId?: string;
  createdAt: Date;
}

export interface PlanProduct {
  id: string;
  product?: Product;
  productId: string;
  countryPriceGroupId?: string;
  sessionLinkId?: string;
  createdAt: Date;
}

export interface Plan {
  id: string;
  sessionId: string;
  organizationId: string;
  products: PlanProduct[];
  offers: Offer[];
  incentiveMethod: IncentiveMethod;
  billingInformation?: BillingInformation;
  billingInformationId?: string;
  billingMethod?: BillingMethod;
  paymentMethodId?: string;
  assignedToId?: string;
  priceThierId?: string;
  status: PlanStatus;
  version: number;
  referralActivated: boolean;
  referralIncentive?: Incentive;
  referralText?: string;
}

export interface PublicReferral {
  activated: boolean;
  incentive?: Incentive;
  text?: string;
}

export const usePlan = (id?: string) => {
  return useQuery<Plan>(['plans', id], queryGet(`/v2/plans/${id}`), {
    enabled: !!id,
  });
};

export const useAcceptPlan = (id?: string) => {
  return useMutation<Plan, void, { cgsContentId: string }>(
    queryUpdate(`/v2/plans/${id}/accept`),
  );
};

export const useUpdateIncentiveInPlan = (planId?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Plan, any, Incentive>(
    queryReplace(`/v2/plans/${planId}/incentives`),
    {
      onSuccess: (plan: Plan) => {
        setTimeout(() => {
          queryClient.invalidateQueries([
            'sessions',
            plan.sessionId,
            'session-link-incentives',
          ]);
          queryClient.invalidateQueries([
            'sessions',
            plan.sessionId,
            'incentive',
          ]);
          queryClient.invalidateQueries(['plans', plan.id]);
        }, 300);
      },
    },
  );
};

export const useAddOfferToPlan = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Plan, undefined, { offerId: string }>(
    queryCreate(`/v2/plans/${id}/offers`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['plans', id]);
      },
    },
  );
};

export const useUpdateOptionsInPlan = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Plan, undefined, { optionsIds: string[] }>(
    queryReplace(`/v2/plans/${id}/options`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['plans', id]);
      },
    },
  );
};

interface UpdatePlanDto {
  billingMethod?: BillingMethod;
  paymentMethodId?: string;
  referralIncentive?: Incentive;
  referralActivated?: boolean;
  referralText?: string;
}

export const useUpdatePlan = (planId?: string) => {
  const queryClient = useQueryClient();

  return useMutation<Plan, any, UpdatePlanDto>(
    queryUpdate(`/v2/plans/${planId}`),
    {
      onSuccess: (plan: Plan) => {
        queryClient.setQueryData(['plans', plan.id], plan);
      },
    },
  );
};

export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  return useMutation<Product, any, Partial<Product>>(
    queryCreate(`/v2/products`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['plans']);
      },
    },
  );
};

interface AddProductToPlanDto {
  productId: string;
  sessionLinkId?: string;
  countryPriceGroupId?: string;
}

export const useAddProductToPlan = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Plan, any, AddProductToPlanDto>(
    queryCreate(`/v2/plans/${id}/products`),
    {
      onSuccess: (plan: Plan) => {
        queryClient.setQueryData(['plans', plan.id], plan);
      },
    },
  );
};

export const useRemoveProductFromPlan = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Plan, any, AddProductToPlanDto>(
    queryDelete(`/v2/plans/${id}/products`),
    {
      onSuccess: (plan: Plan) => {
        queryClient.setQueryData(['plans', plan.id], plan);
        setTimeout(() => {
          queryClient.invalidateQueries([
            'sessions',
            plan.sessionId,
            'session-link-incentives',
          ]);
          queryClient.invalidateQueries([
            'sessions',
            plan.sessionId,
            'incentives',
          ]);
          queryClient.invalidateQueries(['incentives']);
        }, 500);
      },
    },
  );
};

/**
 * `locale` asks the api for the product titles in that language instead of the
 * language of the interface.
 */
export const useCatalog = (locale?: string) => {
  return useQuery<Product[]>(locale ? ['products', locale] : 'products', () =>
    queryFetch<Product[]>({
      url: `${API_URL}/v2/products`,
      method: 'GET',
      headers: locale ? { 'Accept-Language': locale } : undefined,
    }),
  );
};

export const usePlanIncentives = (id?: string, duration?: number) => {
  return useQuery<PlanIncentive[]>(
    ['plans', id, 'incentives', duration],
    queryGet(`/v2/plans/${id}/incentives`, duration ? { duration } : undefined),
    {
      enabled: !!id,
    },
  );
};

export const useAssignPlanToMe = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Plan, Error, void>(
    queryCreate(`/v2/plans/${id}/assignedTo/me`),
    {
      onSuccess: (plan: Plan) => {
        queryClient.setQueryData(['plans', id], plan);
      },
    },
  );
};

export const usePublicSessionReferral = (id?: string) => {
  return useQuery<PublicReferral>(
    ['sessions', id, 'referral'],
    queryGet(`/v2/sessions/${id}/referral`),
    {
      enabled: !!id,
    },
  );
};

interface UpdatePriceForCountryAndLinkDto {
  type: ProductType;
  amount?: number | null;
  sessionLinkId?: string | null;
  countryPriceGroup?: string | null;
}

export const useUpdatePriceForCountryAndLink = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<Plan, Error, UpdatePriceForCountryAndLinkDto>(
    queryCreate(`/v2/plans/${id}/country-link-price`),
    {
      onSuccess: (plan: Plan) => {
        queryClient.setQueryData(['plans', id], plan);
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query) && query.length >= 2
              ? query[0] === 'sessions' && query[2] === 'incentive'
              : false,
        });
      },
    },
  );
};
